import { memo, useState } from "react";
import "Components/ConfigLayout/ConfigNav/ConfigNav.less";
import { NavLink, useMatch, useNavigate } from "react-router-dom";
import { Navigation } from "@clintonelec/react-storybook";
import { CameraSubCategories, Categories, Menus } from "Data/Objects/Category";
import { selectNavbarCollapsed } from "Data/Redux/Slices/UserSettings";
import { useAppSelector } from "Data/Redux/Store";
import { selectSystemInformation } from "Data/Redux/Slices/Settings/System/Information";
import { isCategory } from "Data/Utils/Guards";

const icons = {
	[ Categories.CAMERA ]: "camera-cctv",
	[ Categories.AUDIO ]: "volume",
	[ Categories.USER ]: "user",
	[ Categories.NETWORK ]: "globe",
	[ Categories.SYSTEM ]: "gears",
	[ Categories.STORAGE ]: "hard-drive",
	[ Categories.EVENT ]: "siren-on",
	[ Categories.RECORD ]: "film"
};

function ConfigNav() {
	const match = useMatch("/setup/:currentCategory?/:currentSubCategory?");
	const currentCategory = match?.params?.currentCategory?.toUpperCase() ?? Categories.CAMERA;
	const [ activeCategory, setActiveCategory ] = useState(
		isCategory(currentCategory) ? Categories[ currentCategory ] : Categories.CAMERA
	);

	const { model } = useAppSelector(selectSystemInformation);
	const navCollapsed = useAppSelector(selectNavbarCollapsed);
	const navigate = useNavigate();
	const conditionalCategories: Record<string, boolean> = {
		[ CameraSubCategories.DYNAMIC_ZOOM ]: model.includes("PV")
	};

	const handleClick = (category: string) => () => {
		setActiveCategory(category as Categories);
	};

	const handleSubCategoryClick = (link: string) => () => {
		navigate(link);
	};

	const renderItems = (category: string, item: string) => {
		const formattedItem = item.toLowerCase().replace(/[\s/]+/g, text => text === " " ? "_" : "");
		const link = `/setup/${ category.toLowerCase() }/${ formattedItem }`;

		if (conditionalCategories[ item ] === false) {
			return;
		}

		return (
			<Navigation.Item key={ item } onItemClick={ handleSubCategoryClick(link) }>
				<NavLink to={ link }>{ item }</NavLink>
			</Navigation.Item>
		);
	};

	const renderMenus = (category: string) => Object.values<string>(Menus[ category as Categories ]).map(item => {
		if (conditionalCategories[ category ] === false) {
			return;
		}

		return renderItems(category, item);
	});

	const renderCategories = (category: string) => {
		if (conditionalCategories[ category ] === false) {
			return;
		}

		return (
			<Navigation.Category
				active={ activeCategory === category }
				icon={ icons[ category as Categories ] }
				title={ Categories[ category as Categories ] }
				onCategoryClick={ handleClick(category) }
				key={ category }
			>
				{ renderMenus(category) }
			</Navigation.Category>
		);
	};

	return (
		<Navigation collapsed={ navCollapsed }>
			{ Object.values(Categories).map(renderCategories) }
		</Navigation>
	);
}

export default memo(ConfigNav);
